import type {DashboardItem, LayoutHint} from "./services/dashboardsApi";

export type GalleryFilter = "All" | "Built-in" | "Yours";
export const GALLERY_FILTERS: GalleryFilter[] = ["All", "Built-in", "Yours"];

export type GallerySections = {
    builtIn: DashboardItem[];
    mine: DashboardItem[];
    showBuiltIn: boolean;
    showMine: boolean;
    /** Nothing visible for the current search + filter. */
    noMatch: boolean;
};

export function matchesQuery(d: DashboardItem, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return d.title.toLowerCase().includes(q) || (d.description ?? "").toLowerCase().includes(q);
}

/** Search + segmented filter, as in the design's DshGallery. */
export function filterGallery(
    builtIn: DashboardItem[],
    mine: DashboardItem[],
    query: string,
    filter: GalleryFilter,
): GallerySections {
    const b = builtIn.filter(d => matchesQuery(d, query));
    const m = mine.filter(d => matchesQuery(d, query));
    // Under "All" an empty Built-in section is just left out; picked explicitly, it shows its empty state.
    const showBuiltIn = filter === "Built-in" || (filter === "All" && b.length > 0);
    // "Yours" stays visible when empty so its empty state can invite a first dashboard.
    const showMine = filter !== "Built-in";
    const visible = (filter !== "Yours" ? b.length : 0) + (filter !== "Built-in" ? m.length : 0);
    return {builtIn: b, mine: m, showBuiltIn, showMine, noMatch: visible === 0 && query.trim() !== ""};
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * en-GB short date, e.g. "27 Jul 2026". Built by hand because newer ICU data
 * renders September as "Sept" for en-GB.
 */
export function formatDashboardDate(value: string | null | undefined): string | null {
    if (!value) return null;
    // Plain dates (YYYY-MM-DD) are calendar days, not instants: don't shift them by time zone.
    const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (plain) {
        return `${Number(plain[3])} ${MONTHS[Number(plain[2]) - 1]} ${plain[1]}`;
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

// region thumbnail
export type ThumbKind = "line" | "bar" | "hbar" | "donut" | "table" | "kpi" | "text" | "block";
export type ThumbCell = {
    kind: ThumbKind;
    /** Columns on the miniature's 12-column grid: Superset's own width, so rows match the dashboard. */
    span: number;
    /** First cell of a dashboard row: starts a new grid row even when the previous row has room. */
    newRow: boolean;
};

/** Cells drawn; the 104px miniature crops whatever rows don't fit (it is the top of the dashboard). */
export const THUMB_MAX_CELLS = 12;
export const THUMB_COLUMNS = 12;

/** Hermes viz or Superset viz_type → miniature shape. */
export function vizToThumbKind(viz: string): ThumbKind {
    const v = viz.toLowerCase();
    if (/markdown|insight|handlebars|text/.test(v)) return "text";
    if (/big_number|kpi|metric|number/.test(v)) return "kpi";
    if (/table|pivot|list/.test(v)) return "table";
    if (/pie|donut|sunburst|funnel|gauge/.test(v)) return "donut";
    if (/heatmap|treemap|sankey|custom|world_map|map/.test(v)) return "block";
    if (/hbar|horizontal|bar_h/.test(v)) return "hbar";
    if (/bar|histogram|waterfall|box_plot|variance|combo|pareto|mixed/.test(v)) return "bar";
    if (/line|area|timeseries|time|smooth|step|scatter|bubble/.test(v)) return "line";
    return "block";
}

/** Superset width (1–12), clamped; unknown widths take the full row. */
export function widthToSpan(width: number): number {
    if (!Number.isFinite(width) || width <= 0) return THUMB_COLUMNS;
    return Math.min(THUMB_COLUMNS, Math.max(1, Math.round(width)));
}

/**
 * Cells in layout order. With row numbers (current Hermes) a new dashboard row always starts a new
 * grid row; without them (older listings) cells simply wrap at 12 columns.
 */
export function layoutToThumbCells(hints: LayoutHint[]): ThumbCell[] {
    let prevRow: number | null | undefined;
    return hints.slice(0, THUMB_MAX_CELLS).map((h, i) => {
        const row = h.row ?? null;
        const newRow = i > 0 && row !== null && prevRow !== null && row !== prevRow;
        prevRow = row;
        return {kind: vizToThumbKind(h.viz), span: widthToSpan(h.width), newRow};
    });
}

/** DashGen dashboards carry no layout: a generic KPI strip + two charts. */
export const GENERIC_THUMB: ThumbCell[] = [
    {kind: "kpi", span: 3, newRow: false},
    {kind: "kpi", span: 3, newRow: false},
    {kind: "kpi", span: 3, newRow: false},
    {kind: "kpi", span: 3, newRow: false},
    {kind: "line", span: 6, newRow: true},
    {kind: "bar", span: 6, newRow: false},
];
// endregion

// region card / header text
export const VIEW_ONLY = "🔒 View only";

/** Card meta line: "🔒 View only · N charts · updated 27 Jul 2026" (unknown parts left out). */
export function dashboardMeta(d: DashboardItem): string[] {
    const parts: string[] = [];
    if (d.source === "hermes") parts.push(VIEW_ONLY);
    if (d.chartCount !== null) parts.push(`${d.chartCount} ${d.chartCount === 1 ? "chart" : "charts"}`);
    const updated = formatDashboardDate(d.updatedAt);
    if (updated) parts.push(`updated ${updated}`);
    return parts;
}

/** Detail subtitle: "Data as of … · {source_label} · Updated …" (unknown parts left out). */
export function dashboardSubtitle(d: DashboardItem): string {
    const parts: string[] = [];
    const asOf = formatDashboardDate(d.dataAsOf);
    if (asOf) parts.push(`Data as of ${asOf}`);
    if (d.sourceLabel) parts.push(d.sourceLabel);
    const updated = formatDashboardDate(d.updatedAt);
    if (updated) parts.push(`Updated ${updated}`);
    return parts.join(" · ");
}
// endregion
